import React, { useState } from 'react';
import './Reviews.css';

interface Review {
  name: string;
  role: string;
  rating: number;
  text: string;
}

const parentReviews: Review[] = [
  {
    name: 'Emma Williams',
    role: 'Parent',
    rating: 5,
    text: 'As a parent, I observe my child developing respect, discipline, and perseverance through their training. These values, taught with care and tradition, will serve them throughout their life.'
  },
  {
    name: 'James Cooper',
    role: 'Parent',
    rating: 5,
    text: "My son has transformed since joining. His confidence has grown enormously, and the instructors genuinely care about each child's progress. Best decision we ever made as a family."
  },
  {
    name: 'Lisa Patel',
    role: 'Parent',
    rating: 5,
    text: 'What I value most is how the club teaches children to be respectful and disciplined — not just in class, but at home and school too. The instructors lead by example, every single session.'
  },
];

const studentReviews: Review[] = [
  {
    name: 'Sarah Johnson',
    role: 'Black Belt',
    rating: 5,
    text: 'The discipline and respect cultivated through Taekwon-Do training extends far beyond the dojang. The instructors demonstrate patience and wisdom, guiding each student on their personal journey of development.'
  },
  {
    name: 'Michael Chen',
    role: 'Red Belt',
    rating: 5,
    text: 'I began training to learn self-defense, but discovered a path of personal growth. The physical development is significant, and I have found a community built on mutual respect and shared dedication to the art.'
  },
  {
    name: 'David Martinez',
    role: 'Black Belt',
    rating: 5,
    text: 'The training challenges both body and mind, while preserving the traditional values of Taekwon-Do. This practice has become an essential part of my personal development and well-being.'
  }
];

interface CarouselProps {
  reviews: Review[];
  title: string;
}

const ReviewCarousel: React.FC<CarouselProps> = ({ reviews, title }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  return (
    <div className="review-column">
      <h3 className="review-column-title">{title}</h3>
      <div className="reviews-carousel">
        <button
          className="carousel-button prev"
          onClick={() => setCurrentIndex((i) => (i - 1 + reviews.length) % reviews.length)}
          aria-label="Previous review"
        >‹</button>

        <div className="review-card">
          <div className="review-rating">
            {[...Array(reviews[currentIndex].rating)].map((_, i) => (
              <span key={i} className="star">★</span>
            ))}
          </div>
          <p className="review-text">"{reviews[currentIndex].text}"</p>
          <div className="review-author">
            <h4 className="review-name">{reviews[currentIndex].name}</h4>
            <p className="review-belt">{reviews[currentIndex].role}</p>
          </div>
        </div>

        <button
          className="carousel-button next"
          onClick={() => setCurrentIndex((i) => (i + 1) % reviews.length)}
          aria-label="Next review"
        >›</button>
      </div>

      <div className="review-indicators">
        {reviews.map((_, index) => (
          <button
            key={index}
            className={`indicator ${index === currentIndex ? 'active' : ''}`}
            onClick={() => setCurrentIndex(index)}
            aria-label={`Go to review ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

interface ReviewsProps {
  onNavigate: (page: string) => void;
}

const Reviews: React.FC<ReviewsProps> = ({ onNavigate }) => {
  return (
    <section id="reviews" className="reviews-section">
      <div className="container">
        <h2 className="section-title">What Our Community Says</h2>
        <p className="section-subtitle">Real experiences from our Taekwon-Do family</p>

        <div className="reviews-columns">
          <ReviewCarousel title="What Parents Say" reviews={parentReviews} />
          <ReviewCarousel title="What Students Say" reviews={studentReviews} />
        </div>

        <div className="feedback-cta">
          <p className="feedback-cta-text">Have your own experience to share?</p>
          <button className="feedback-cta-button" onClick={() => onNavigate('feedback')}>
            Share Your Experience
          </button>
        </div>
      </div>
    </section>
  );
};

export default Reviews;
